<template>
  <HealthLogout v-model="modals.logout" />

  <nav class="flex flex-col h-full">
    <section
      v-tooltip.right="t('menu')"
      class="cursor-pointer min-w-8 min-h-8 h-[4.875rem] gap-x-4 flex items-center border-b border-gray-300 dark:border-dark-400 transition-all duration-300 justify-start"
      :class="open ? 'pl-8' : 'pl-[2.275rem]'"
      @click="toggleFixed"
    >
      <Icon
        icon="zondicons:menu"
        class="min-w-[1.2rem] min-h-[1.2rem] transition-all duration-300"
      />
      <span
        class="transition-all duration-200 overflow-hidden whitespace-nowrap block"
        :class="open ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
      >
      </span>
    </section>

    <section
      class="flex-1 transition-all duration-300"
      :class="open ? 'overflow-y-auto' : '!overflow-hidden'"
    >
      <ul class="flex flex-col pb-3 pt-2" :class="open ? '' : 'gap-y-2'">
        <li
          v-for="(link, idx) in filteredMenu"
          :key="idx"
          class="text-[13px] font-medium text-gray-600 dark:text-gray-300 transition-all ease-in-out duration-300"
        >
          <component
            :is="link.to ? 'RouterLink' : 'div'"
            v-tooltip.right="{ value: t(link.label, 2), class: '!max-w-full' }"
            :to="link.to"
            class="cursor-pointer transition-all duration-300 ease-in-out"
            :class="[
              open
                ? 'flex items-center gap-x-2 px-8 py-3 dark:hover:bg-dark-800 hover:bg-gray-100 text-gray-600 dark:text-gray-300 hover:text-primary-500 hover:dark:text-primary-200'
                : 'flex flex-col items-center justify-center gap-y-1.5 mx-2 py-2.5 px-1 rounded-lg text-center text-gray-600 dark:text-gray-300 dark:hover:bg-dark-800 hover:bg-gray-100 hover:text-primary-500 hover:dark:text-primary-200 border border-transparent',
              checkActiveRoute(link)
                ? open
                  ? 'font-semibold text-primary-500 dark:!text-primary-200'
                  : 'font-semibold text-primary-500 dark:!text-primary-200 bg-primary-50/10 border-transparent dark:bg-primary-950/10 dark:border-transparent'
                : '',
              link?.wrapperClass,
            ]"
            @click.prevent="(openSubMenu($event, link, idx), link.action && link.action())"
          >
            <Icon
              :icon="link.icon"
              class="min-w-[20px] min-h-[20px] md:min-w-[1.4rem] md:min-h-[1.4rem] transition-all duration-300"
              :class="[
                checkActiveRoute(link) ? 'text-primary-500 dark:text-primary-200' : '',
                link?.iconClass,
              ]"
            />

            <transition name="fade-text" mode="out-in">
              <span
                v-if="open"
                class="overflow-hidden whitespace-nowrap block text-[13px] transition-all duration-300"
                :class="[checkActiveRoute(link) ? 'text-primary-500 dark:text-primary-200' : '']"
              >
                {{ t(link.label, 2) }}
              </span>
              <span
                v-else
                class="text-[11px] leading-tight font-medium w-full tracking-tighter truncate px-0.5 mt-1 transition-all duration-300 block"
                :class="[checkActiveRoute(link) ? 'text-primary-500 dark:text-primary-200' : '']"
              >
                {{ t(link.label, 2) }}
              </span>
            </transition>

            <Icon
              v-if="link?.childs && open"
              icon="ep:arrow-down-bold"
              class="min-w-[0.5rem] min-h-[0.5rem] transition-transform duration-300 ml-auto"
              :class="{ 'rotate-180': isOpen(idx) }"
            />
          </component>

          <ul
            v-show="isOpen(idx)"
            class="flex flex-col gap-4 items-start relative my-2 text-xs transition-[margin] duration-300"
            :class="open ? 'ms-10' : 'ms-7'"
          >
            <li
              v-for="(linkChild, idxChild) in link?.childs"
              :key="idxChild"
              class="relative flex flex-col items-start gap-1 w-full transition-all ease-in-out duration-300"
              role="menuitem"
            >
              <component
                :is="linkChild.to ? 'RouterLink' : 'div'"
                v-tooltip.right="{ value: t(linkChild.label, 2), class: '!max-w-full' }"
                :to="linkChild.to"
                :class="[
                  {
                    'font-semibold text-primary-500 dark:!text-primary-200':
                      checkActiveRoute(linkChild),
                  },
                ]"
                class="flex items-center gap-2 group hover:text-primary-500 hover:dark:text-primary-200 text-gray-600 dark:text-gray-300 w-full cursor-pointer pr-8"
                @click.prevent="
                  (openSubMenu($event, linkChild, idx * 100 + idxChild),
                  linkChild.action && linkChild.action())
                "
              >
                <span
                  class="min-w-8 min-h-8 flex items-center justify-center rounded-md border border-gray-300 dark:border-dark-600 group-hover:border-primary-500"
                  :class="[
                    {
                      'border-primary-500 dark:!border-primary-200': checkActiveRoute(linkChild),
                    },
                  ]"
                >
                  <Icon :icon="linkChild.icon" class="min-h-[1.2rem] min-w-[1.2rem]" />
                </span>
                <span
                  class="transition-all duration-200 overflow-hidden whitespace-nowrap block"
                  :class="open ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
                >
                  {{ t(linkChild.label, 2) }}
                </span>
                <Icon
                  v-if="linkChild?.childs && open"
                  icon="ep:arrow-down-bold"
                  class="min-w-[0.5rem] min-h-[0.5rem] transition-transform duration-300 ml-auto"
                  :class="{ 'rotate-180': isOpen(idx * 100 + idxChild) }"
                />
              </component>

              <ul
                v-if="linkChild?.childs"
                v-show="isOpen(idx * 100 + idxChild)"
                class="flex flex-col gap-3 items-start relative mt-1 mb-1 text-xs transition-[margin] duration-300 w-full"
                :class="open ? 'ms-6' : 'ms-7'"
              >
                <li
                  v-for="(linkGrandChild, idxGrandChild) in linkChild?.childs"
                  :key="idxGrandChild"
                  class="relative flex items-center w-full transition-all ease-in-out duration-300"
                  role="menuitem"
                >
                  <RouterLink
                    v-tooltip.right="{ value: t(linkGrandChild.label, 2), class: '!max-w-full' }"
                    :to="linkGrandChild.to"
                    :class="[
                      {
                        'font-semibold text-primary-500 dark:!text-primary-200':
                          checkActiveRoute(linkGrandChild),
                      },
                    ]"
                    class="flex items-center gap-2 group hover:text-primary-500 hover:dark:text-primary-200 text-gray-600 dark:text-gray-300 w-full"
                  >
                    <span
                      class="min-w-8 min-h-8 flex items-center justify-center rounded-md border border-gray-300 dark:border-dark-600 group-hover:border-primary-500"
                      :class="[
                        {
                          'border-primary-500 dark:!border-primary-200':
                            checkActiveRoute(linkGrandChild),
                        },
                      ]"
                    >
                      <Icon :icon="linkGrandChild.icon" class="min-h-[1.2rem] min-w-[1.2rem]" />
                    </span>
                    <span
                      class="transition-all duration-200 overflow-hidden whitespace-nowrap block"
                      :class="open ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
                    >
                      {{ t(linkGrandChild.label, 2) }}
                    </span>
                  </RouterLink>
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </section>
  </nav>
</template>

<script setup lang="ts">
  import { useRoute, useRouter } from "vue-router";
  import { ref, computed } from "vue";
  import { Icon } from "@iconify/vue";
  import { useI18n } from "vue-i18n";
  import { useSidebar } from "@/shared/composables/useSidebar";
  import { usePermission } from "@/shared/composables/usePermission";
  import HealthLogout from "@/shared/components/utils/HealthLogout.vue";

  type MenuItem = {
    label: string;
    icon: string;
    to?: string;
    wrapperClass?: string;
    iconClass?: string;
    action?: () => void;
    childs?: MenuItem[];
  };

  const router = useRouter();
  const { t } = useI18n();
  const route = useRoute();
  const popover = ref();
  const { toggleFixed, open, toggleMenu, isOpen } = useSidebar();
  const modals = ref({ logout: false });

  const baseMenu = ref<MenuItem[]>([
    // { label: "dashboard", icon: "mingcute:grid-2-fill", to: "/" },
    { label: "exam", icon: "famicons:medical-outline", to: "/exams" },
    {
      label: "sidebar_protocols",
      icon: "healthicons:clinical-f-outline-24px",
      to: "/clinical-protocols",
    },
    { label: "sidebar_masks", icon: "radix-icons:file-text", to: "/report-masks" },
    { label: "sidebar_phrases", icon: "streamline:cursor-click", to: "/shortcut-phrases" },
    { label: "user", icon: "ci:users-group", to: "/users" },
    { label: "institution", icon: "uil:building", to: "/institutions" },
    {
      label: "ris",
      icon: "fluent-emoji-high-contrast:radioactive",
      childs: [
        {
          label: "medical_specialty",
          icon: "icon-park-outline:medical-box",
          to: "/medical-specialties",
        },
        {
          label: "acquisition_modality",
          icon: "hugeicons:study-desk",
          to: "/acquisition-modalities",
        },
        {
          label: "manufacturer",
          icon: "material-symbols:precision-manufacturing",
          to: "/manufacturers",
        },
        { label: "local", icon: "lucide:hospital", to: "/locations" },
        { label: "requesting_doctor", icon: "healthicons:doctor", to: "/requesting-doctors" },
        { label: "exam_type", icon: "healthicons:medical-records", to: "/exam-types" },
        { label: "body_part", icon: "ion:body-outline", to: "/body-parts" },
        { label: "procedure", icon: "flowbite:clipboard-outline", to: "/procedures" },
        { label: "create_exam", icon: "icon-park-outline:document-folder", to: "/exam/register" },
        {
          label: "occupational",
          icon: "healthicons:clinical-fe-outline",
          childs: [
            { label: "company", icon: "mdi:domain", to: "/companies" },
            { label: "role", icon: "hugeicons:doctor-03", to: "/roles" },
            { label: "sector", icon: "mingcute:department-line", to: "/sectors" },
          ],
        },
      ],
    },
    {
      label: "setting",
      icon: "uil:cog",
      childs: [{ label: "profile", icon: "lucide:user-round-cog", to: "/profiles" }],
    },
    { label: "logs", icon: "hugeicons:audit-02", to: "/logs" },
    {
      label: "logout",
      icon: "solar:logout-2-outline",
      action: () => (modals.value.logout = true),
      wrapperClass: "!text-red-500 dark:!text-red-600 !font-medium",
      iconClass: "!text-red-500 dark:!text-red-600",
    },
  ]);

  const filteredMenu = computed(() => {
    const allRoutes = router.getRoutes();

    return baseMenu.value.reduce((visibleMenu, item) => {
      if (item.childs) {
        const visibleChilds = item.childs.reduce((filteredChilds, child) => {
          if (child.childs) {
            const visibleGrandChilds = child.childs.filter((grandChild) => {
              const grandChildRoute = allRoutes.find((r) => r.path === grandChild.to);
              const permissions = grandChildRoute?.meta?.permissions as string[];
              if (!permissions || permissions.length === 0) return true;
              return usePermission(...permissions);
            });
            if (visibleGrandChilds.length > 0) {
              filteredChilds.push({ ...child, childs: visibleGrandChilds });
            }
          } else if (child.to) {
            const childRoute = allRoutes.find((r) => r.path === child.to);
            const permissions = childRoute?.meta?.permissions as string[];
            if (!permissions || permissions.length === 0) filteredChilds.push(child);
            else if (usePermission(...permissions)) filteredChilds.push(child);
          } else {
            filteredChilds.push(child);
          }
          return filteredChilds;
        }, [] as MenuItem[]);

        if (visibleChilds.length > 0) {
          visibleMenu.push({ ...item, childs: visibleChilds });
        }
      } else if (item.to) {
        const itemRoute = allRoutes.find((r) => r.path === item.to);
        const permissions = itemRoute?.meta?.permissions as string[];
        if (!permissions || permissions.length === 0) {
          visibleMenu.push(item);
        } else if (usePermission(...permissions)) {
          visibleMenu.push(item);
        }
      } else {
        visibleMenu.push(item);
      }

      return visibleMenu;
    }, [] as MenuItem[]);
  });

  const checkActiveRoute = (item) => {
    if (item.to === "/") return route.path === "/";

    return route.path.startsWith(item.to);
  };

  const openSubMenu = (event, link, idx) => {
    if (link.childs) toggleMenu(idx);

    return null;
  };
</script>

<style scoped>
  .fade-text-enter-active,
  .fade-text-leave-active {
    transition: opacity 0.15s ease;
  }
  .fade-text-enter-from,
  .fade-text-leave-to {
    opacity: 0;
  }
  .fade-text-enter-to,
  .fade-text-leave-from {
    opacity: 1;
  }
</style>
